import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown, { type Components } from "react-markdown";
import rehypeSlug from "rehype-slug";
import remarkGfm from "remark-gfm";
import { getComplianceContent, getInitialComplianceContent, getComplianceMeta, type ComplianceVersionItem } from "../../api/compliance";

interface ComplianceDocumentProps {
    slug: string;
    title: string;
    description?: React.ReactNode;
}

const markdownComponents: Components = {
    a: ({ children, ...props }) => <a {...props}>{children}</a>,
    h1: ({ children, ...props }) => <h1 {...props}>{children}</h1>,
    h2: ({ children, ...props }) => <h2 {...props}>{children}</h2>,
    ul: (props) => <ul {...props} />,
    li: (props) => <li {...props} />,
};

function goBack(navigate: ReturnType<typeof useNavigate>) {
    if (window.history.length > 1) {
        navigate(-1);
    } else {
        navigate("/");
    }
}

export default function ComplianceDocument({ slug, title, description }: ComplianceDocumentProps) {
    const navigate = useNavigate();
    const [content, setContent] = useState<string>(() => getInitialComplianceContent(slug));
    const [version, setVersion] = useState<string>("");
    const [updatedAt, setUpdatedAt] = useState<string>("");
    const [versions, setVersions] = useState<ComplianceVersionItem[]>([]);

    useEffect(() => {
        let cancelled = false;

        setContent(getInitialComplianceContent(slug));
        setVersion("");
        setUpdatedAt("");
        setVersions([]);

        void getComplianceMeta(slug).then((meta) => {
            if (!cancelled && meta) {
                setVersion(meta.version);
                setUpdatedAt(meta.effective_at);
                setVersions(meta.versions);
            }
            void getComplianceContent(slug, meta?.hash).then((md) => {
                if (!cancelled && md.trim()) setContent(md);
            });
        });

        return () => {
            cancelled = true;
        };
    }, [slug]);

    const fmtDate = (iso: string) =>
        new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

    const pageStyle: React.CSSProperties = {
        minHeight: "100dvh",
        background: "var(--c-cream)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "var(--s-7) var(--gutter)",
    };

    const colStyle: React.CSSProperties = {
        width: "100%",
        maxWidth: "720px",
        display: "flex",
        flexDirection: "column",
        gap: "var(--s-5)",
    };

    return (
        <div style={pageStyle}>
            <div style={colStyle}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 var(--s-1)" }}>
                    <button
                        type="button"
                        onClick={() => { goBack(navigate); }}
                        className="btn btn--ghost"
                        style={{ fontSize: "13px", padding: "10px 18px" }}
                    >
                        ← Back
                    </button>
                    <a href="/" className="logo" aria-label="kivo.skin home">
                        kivo<span className="dot" />
                    </a>
                </div>

                <div className="compliance-card">
                    <header style={{ textAlign: "center", paddingBottom: "var(--s-7)", marginBottom: "var(--s-7)" }}>
                        <p className="t-label" style={{ marginBottom: "var(--s-3)" }}>Legal</p>
                        <h1 className="t-h2" style={{ margin: 0, color: "var(--ink)" }}>{title}</h1>

                        {(version || updatedAt) && (
                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "var(--s-2)", marginTop: "var(--s-4)" }}>
                                {version && (
                                    <span className="pill">
                                        <span className="dot" />
                                        Version {version}
                                    </span>
                                )}
                                {updatedAt && (
                                    <span className="pill">
                                        Updated {new Date(updatedAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                                    </span>
                                )}
                            </div>
                        )}

                        {description && (
                            <p className="t-small t-mute" style={{ marginTop: "var(--s-5)", maxWidth: "52ch", marginInline: "auto" }}>
                                {description}
                            </p>
                        )}
                    </header>

                    {versions.length > 0 && (
                        <details
                            style={{
                                border: "1px solid var(--rule)",
                                borderRadius: "var(--r-md)",
                                padding: "var(--s-3) var(--s-4)",
                                background: "var(--c-cream)",
                                marginBottom: "var(--s-5)",
                            }}
                        >
                            <summary
                                style={{ cursor: "pointer", listStyle: "revert", fontWeight: 600 }}
                                className="t-small"
                            >
                                Version history ({versions.length})
                            </summary>
                            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "var(--s-3)" }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: "left", padding: "var(--s-2) var(--s-1)" }} className="t-small t-mute">Version</th>
                                        <th style={{ textAlign: "right", padding: "var(--s-2) var(--s-1)" }} className="t-small t-mute">Effective date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {versions.map((v) => (
                                        <tr key={v.version} style={{ borderTop: "1px solid var(--rule)" }}>
                                            <td style={{ padding: "var(--s-2) var(--s-1)" }} className="t-small">
                                                {v.version}
                                                {v.version === version && (
                                                    <span className="t-mute"> (current)</span>
                                                )}
                                            </td>
                                            <td style={{ padding: "var(--s-2) var(--s-1)", textAlign: "right" }} className="t-small t-mute">
                                                {fmtDate(v.effective_at)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </details>
                    )}

                    <hr className="rule" />

                    <div className="compliance-prose" style={{ marginTop: "var(--s-7)" }}>
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeSlug]}
                            components={markdownComponents}
                        >
                            {content}
                        </ReactMarkdown>
                    </div>

                    <hr className="rule" style={{ marginTop: "var(--s-7)" }} />

                    <footer style={{ paddingTop: "var(--s-6)", display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--s-4)" }}>
                        <button
                            type="button"
                            onClick={() => { goBack(navigate); }}
                            className="btn btn--ghost"
                        >
                            ← Back
                        </button>
                        <p className="t-small t-mute" style={{ textAlign: "center", margin: 0 }}>
                            This document is provided for informational purposes. © {new Date().getFullYear()} kivo.skin
                        </p>
                    </footer>
                </div>
            </div>
        </div>
    );
}
